/**
 * Saved versions, in the Source dock.
 *
 * **Versions, never "history".** `src/app/history.ts` is undo/redo within a
 * session; this is the server's list of saved states. Two things called history
 * in one product teaches people that undo reaches back across days, and it does
 * not.
 *
 * The rule this panel is built around: **it must never be the reason work looks
 * lost.** It fetches over a network the editor does not need, so every failure
 * here is cosmetic — and every one of them has to *look* cosmetic. That is why
 * a failed restore keeps the list on screen, a refresh keeps the old rows while
 * the new ones load, and no message here ever says "failed" without also saying
 * the design is fine.
 */

import type { Tier } from "../../shared/tiers";
import { getProject } from "../api/projects";
import { el, formatRelative, replace } from "../hud/dom";
import {
  type CloudStatus,
  describeVersion,
  exactTime,
  explainStatus,
  RESTORE_NOTE,
  restoreLabel,
  retentionNote,
  type VersionRow,
  versionRows,
} from "./versions";

export interface VersionsPanelOptions {
  /** Re-read on every refresh: it changes as the design links, blocks or unlinks. */
  status: () => CloudStatus;
  /** For the retention line, and it moves when somebody upgrades mid-session. */
  tier: () => Tier;
  signedIn: () => boolean;
  /** Bring a version back. Throwing shows its message; resolving refreshes. */
  onRestore: (versionId: string) => Promise<void>;
}

export interface VersionsPanel {
  el: HTMLElement;
  /** Re-read the list, or defer it if nobody can see the panel. */
  refresh(): void;
  destroy(): void;
}

export function createVersionsPanel(opts: VersionsPanelOptions): VersionsPanel {
  const root = el("section", { class: "versions", aria: { label: "Saved versions" } });
  const body = el("div", { class: "versions__body" });
  /**
   * Errors live outside `body`, permanently.
   *
   * The scaffold rendered a failed restore by replacing the whole panel, which
   * deleted every row — so the one moment you most want the list is the moment
   * it disappeared. Keeping the two regions separate makes that unrepresentable
   * rather than merely fixed.
   */
  const alert = el("p", { class: "versions__note", hidden: true, aria: { live: "polite" } });
  alert.dataset.tone = "error";
  replace(root, el("h3", { class: "versions__title", text: "Saved versions" }), body, alert);

  let abort: AbortController | null = null;
  /** Rejects a stale response that lands after a newer request. */
  let seq = 0;
  let restoring = false;
  let dead = false;
  /**
   * A refresh arrived while nothing could see this. The fetch is deferred to
   * the moment it becomes visible rather than dropped, because "the dock was
   * hidden when you saved" is not a reason to show a stale list later.
   */
  let stale = false;
  /** Kept so the relative times can be re-stamped without re-fetching. */
  let shown: VersionRow[] = [];

  function fail(message: string) {
    alert.textContent = message;
    alert.hidden = false;
  }

  function clearFailure() {
    alert.hidden = true;
    alert.textContent = "";
  }

  function note(text: string, cls = "versions__note"): HTMLElement {
    return el("p", { class: cls, text });
  }

  function row(v: VersionRow): HTMLElement {
    const when = formatRelative(v.when);
    const restore = el("button", {
      class: "versions__restore",
      type: "button",
      text: "Restore",
      // Every button in this list reads "Restore", so without a distinct name a
      // screen reader's button list is a column of identical entries.
      aria: { label: restoreLabel(v, when) },
    }) as HTMLButtonElement;

    /*
     * The live version cannot be restored onto itself, and nothing can be
     * restored while another restore is running.
     *
     * Disabled rather than hidden: a row that silently loses its button reads
     * as a rendering fault, and "this is the one you are looking at" is a
     * useful thing for the list to say.
     */
    restore.disabled = v.current || restoring;
    if (v.current) restore.title = "This is the version you have open";

    restore.addEventListener("click", async () => {
      // Two restores at once would race to become the next revision and one
      // would lose to `where revision = ?` with nothing on screen to say so.
      if (restoring || dead) return;
      restoring = true;
      clearFailure();
      setRestoreEnabled(false);
      const label = restore.textContent;
      restore.textContent = "Restoring…";
      try {
        await opts.onRestore(v.id);
        // Deliberately after the await: the restore saved, which creates a
        // version, so the list on screen is now one row short of the truth.
        load();
      } catch (err) {
        fail((err as Error)?.message || "Could not restore that version. Nothing changed.");
      } finally {
        restoring = false;
        // The node may have been replaced by `load()` above, in which case
        // these touch a detached element and the fresh rows are already right.
        restore.textContent = label;
        setRestoreEnabled(true);
      }
    });

    const item = el(
      "li",
      { class: "versions__item", data: { current: String(v.current) } },
      el(
        "div",
        { class: "versions__text" },
        el("span", {
          class: "versions__when",
          // The live one is named, not decorated: a coloured dot with no words
          // is a thing people have to learn.
          text: v.current ? `${when} · open now` : when,
          // Relative times are friendlier and vaguer. The exact one belongs
          // somewhere, and a title costs nothing. With the time, not just the
          // date: the rows anyone needs to tell apart are the ones saved
          // minutes apart on the same afternoon.
          title: exactTime(v.when),
        }),
        el("span", { class: "versions__detail", text: describeVersion(v) }),
      ),
      restore,
    );
    return item;
  }

  /** Enable or disable every Restore at once, without re-rendering the list. */
  function setRestoreEnabled(on: boolean) {
    for (const btn of body.querySelectorAll<HTMLButtonElement>(".versions__restore")) {
      const isCurrent = btn.closest(".versions__item")?.getAttribute("data-current") === "true";
      btn.disabled = !on || isCurrent;
    }
  }

  function renderRows(rows: VersionRow[]) {
    shown = rows;
    replace(
      body,
      el("ol", { class: "versions__list" }, ...rows.map(row)),
      note(RESTORE_NOTE),
      note(retentionNote(opts.tier()), "versions__note versions__note--quiet"),
    );
  }

  /**
   * The panel is not being rendered at all.
   *
   * `getClientRects()` is empty for a `display: none` ancestor — which is how
   * the docks collapse — and for an element not yet in the document.
   * `document.hidden` covers a background tab. Both mean a fetch buys nothing.
   *
   * Deliberately *not* a viewport test. Scrolled-out-of-view is still rendered,
   * and gating on intersection would leave a panel below the fold blank until
   * somebody scrolled to it — the blank-looks-broken failure this whole file is
   * built to avoid. `offsetParent` was the first attempt and is wrong for a
   * different reason: it is also null for `position: fixed`.
   */
  function unobservable(): boolean {
    return document.hidden || root.getClientRects().length === 0;
  }

  function load() {
    if (dead) return;
    if (unobservable()) {
      observable = false;
      stale = true;
      return;
    }
    observable = true;
    stale = false;

    const status = opts.status();
    const message = explainStatus(status, opts.signedIn());
    if (message !== null || status.kind !== "linked") {
      // Not an error, and the commonest state there is. Cancel any list fetch
      // still running for a project this design is no longer linked to.
      abort?.abort();
      abort = null;
      seq++;
      shown = [];
      replace(body, note(message ?? ""));
      return;
    }

    const mine = ++seq;
    abort?.abort();
    abort = new AbortController();
    /*
     * The old rows stay up while the new ones load.
     *
     * Blanking to "Loading versions…" on every save made the panel flicker
     * through an empty state several times a minute, which reads as the list
     * being lost and refound. `aria-busy` says the same thing without moving
     * anything.
     */
    if (shown.length === 0) replace(body, note("Loading versions…"));
    root.setAttribute("aria-busy", "true");

    getProject(status.projectId, abort.signal)
      .then(({ project, versions }) => {
        if (mine !== seq || dead) return;
        root.removeAttribute("aria-busy");
        clearFailure();
        // A malformed payload must not take the panel down; an empty list is
        // the honest reading of "the server sent no versions".
        const rows = versionRows(Array.isArray(versions) ? versions : [], project?.revision ?? -1);
        if (rows.length === 0) {
          shown = [];
          replace(body, note("No saved versions yet. One is kept every time this design saves."));
          return;
        }
        renderRows(rows);
      })
      .catch((err) => {
        if (mine !== seq || dead) return;
        root.removeAttribute("aria-busy");
        // An abort is us, not a failure.
        if ((err as Error)?.name === "AbortError") return;
        /*
         * Never "failed" on its own. The design and its local copy are fine and
         * this panel is the only thing that could not load — saying so is the
         * difference between a cosmetic glitch and somebody thinking their work
         * is gone.
         */
        if (shown.length > 0) {
          fail("Could not refresh this list. Your work is unaffected.");
        } else {
          replace(body, note("Could not load versions. Your work is unaffected."));
        }
      });
  }

  /** Last known answer from `unobservable()`, so waking can be a transition. */
  let observable = false;

  /*
   * Come back to a panel that is current, and to times that are not lying.
   *
   * A dock left open for an hour keeps saying "2 minutes ago" because the text
   * was stamped once. Re-stamping on the way back costs a re-render of rows we
   * already have, and only when somebody is actually looking.
   *
   * Fires **only on the transition** into being observable. Acting on every
   * notification was an infinite loop: re-rendering the rows changes the
   * panel's height, which is a resize, which re-renders the rows. It hung the
   * page rather than merely wasting work.
   */
  function onWake() {
    if (dead) return;
    const now = !unobservable();
    const became = now && !observable;
    observable = now;
    if (!became) return;
    if (stale) {
      load();
      return;
    }
    if (shown.length > 0) renderRows(shown);
  }

  document.addEventListener("visibilitychange", onWake);
  /*
   * Catches the dock being expanded, which fires no event of its own.
   *
   * A ResizeObserver rather than an IntersectionObserver: a collapsed dock is
   * `display: none`, so expanding it takes this element from a zero box to a
   * real one, which is a resize. Intersection would have made it depend on
   * where the panel happens to sit in the scroll, which is not the question.
   */
  const seen = new ResizeObserver(() => onWake());
  seen.observe(root);

  /*
   * A microtask, not a straight call.
   *
   * The caller appends `panel.el` after this function returns, so a synchronous
   * first load would always measure an element that is not in the document yet,
   * defer, and then depend entirely on an observer firing to ever happen.
   */
  queueMicrotask(load);

  return {
    el: root,
    refresh: load,
    destroy() {
      dead = true;
      abort?.abort();
      seen.disconnect();
      document.removeEventListener("visibilitychange", onWake);
      root.remove();
    },
  };
}
